import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaymentSuccess } from '@/components/checkout';
import { trackCheckoutInitiated } from '@/utils/checkout-analytics';
import { recordFunnelStep, FUNNEL_STEPS } from '@/utils/conversion-funnel';
import logger from '@/utils/logger';

/**
 * CheckoutSuccessPage - Handles post-payment flow from Stripe checkout
 * This page receives Stripe session data and initiates account setup
 */
export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get session ID from URL parameters
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      logger.error('No session ID found in checkout success URL');
      setError('Invalid checkout session. Please try again.');
      return;
    }

    // Track checkout success page visit
    trackCheckoutInitiated('unknown', 'checkout_success_page', {
      session_id: sessionId,
    });

    recordFunnelStep(FUNNEL_STEPS.CHECKOUT_SUCCESS_PAGE_LOADED, {
      session_id: sessionId,
    });

    // Fetch session details from Stripe
    fetchSessionDetails(sessionId);
  }, [searchParams]);

  const fetchSessionDetails = async (sessionId) => {
    try {
      const response = await fetch(`/api/stripe/get-session-details?session_id=${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch session details');
      }

      const data = await response.json();
      
      if (data.success && data.session) {
        setSessionData({
          sessionId: sessionId,
          customerEmail: data.session.customer_details?.email,
          customerPhone: data.session.customer_details?.phone,
          stripeCustomerId: data.session.customer,
          paymentStatus: data.session.payment_status,
          subscriptionId: data.session.subscription,
        });

        // Track successful session retrieval
        recordFunnelStep(FUNNEL_STEPS.SESSION_DETAILS_RETRIEVED, {
          session_id: sessionId,
          customer_email: data.session.customer_details?.email,
          payment_status: data.session.payment_status,
        });
      } else {
        throw new Error(data.error || 'Invalid session data');
      }
    } catch (error) {
      logger.error('Error fetching session details:', error);
      setError('Unable to retrieve payment details. Please contact support.');
    }
  };

  const handleAccountSetupComplete = (authToken) => {
    logger.log('Account setup completed successfully', { hasAuthToken: !!authToken });
    
    // Navigation is handled by AccountSetupFlow component
    // This callback is for any additional cleanup or tracking
  };

  const handleReturnToHome = () => {
    // Check if user has valid session, if not redirect to login
    const sessionSid = sessionStorage.getItem("verificationSid");
    if (sessionSid) {
      navigate('/home', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  const handleContactSupport = () => {
    const supportEmail = 'support@lexi.tips';
    const subject = 'Checkout Issue - Session: ' + (sessionData?.sessionId || 'Unknown');
    const body = `Hi, I encountered an issue during checkout. Session ID: ${sessionData?.sessionId || 'Unknown'}`;
    
    window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-red-600">Checkout Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-2">
            <button
              onClick={handleReturnToHome}
              className="w-full bg-[#F0A7D6] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e391c4] transition-colors"
            >
              Go to Home
            </button>
            <button
              onClick={handleContactSupport}
              className="w-full bg-gray-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-600 transition-colors"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching session data
  if (!sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F0A7D6] mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold mb-2">Processing Payment</h3>
          <p className="text-gray-600">Retrieving your payment details...</p>
        </div>
      </div>
    );
  }

  // Render PaymentSuccess component with session data
  return (
    <PaymentSuccess
      sessionId={sessionData.sessionId}
      customerEmail={sessionData.customerEmail}
      customerPhone={sessionData.customerPhone}
      stripeCustomerId={sessionData.stripeCustomerId}
      onAccountSetupComplete={handleAccountSetupComplete}
    />
  );
}